import { useLayoutEffect, useRef } from 'react'
import { ArrowUpIcon, PlusIcon } from '@heroicons/react/24/outline'

const THEMES = {
  light: {
    textarea: 'max-h-36 text-sm leading-relaxed',
    attach: 'size-7 bg-gray-200 text-gray-600 hover:bg-gray-300',
    attachIcon: 'size-4',
    send: 'size-7',
    sendOn: 'bg-purple-900 text-white hover:bg-purple-950',
    sendOff: 'bg-gray-400 text-white',
    sendIcon: 'size-3.5',
  },
  dark: {
    textarea: 'pr-14 text-xs leading-relaxed text-white placeholder-white/40',
    attach: 'size-6 bg-white/10 text-white/60 hover:bg-white/20 hover:text-white/80',
    attachIcon: 'size-3.5',
    send: 'absolute right-1.5 bottom-1.5 size-6',
    sendOn: 'bg-purple-500 text-white hover:bg-purple-400',
    sendOff: 'bg-white/20 text-white/50',
    sendIcon: 'size-3',
  },
}

type Props = {
  theme: keyof typeof THEMES
  value: string
  rows: number
  disabled: boolean
  autoFocus?: boolean
  onChange: (value: string) => void
  onSend: () => void
  onAttach: (anchor: DOMRect) => void
  onEscape?: () => void
}

/** The edit prompt box: textarea, add-image button and send. Used inline under the chat and
 * inside the dark popup anchored to a detected object. */
export default function EditComposer({
  theme,
  value,
  rows,
  disabled,
  autoFocus,
  onChange,
  onSend,
  onAttach,
  onEscape,
}: Props) {
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const t = THEMES[theme]
  const dark = theme === 'dark'

  useLayoutEffect(() => {
    const field = inputRef.current
    if (!field || dark) return

    field.style.height = 'auto'
    field.style.height = `${Math.min(field.scrollHeight, 144)}px`
  }, [value, dark])

  const attach = (
    <button
      type="button"
      aria-label="Add image"
      disabled={disabled}
      onClick={(event) => onAttach(event.currentTarget.getBoundingClientRect())}
      className={`flex items-center justify-center rounded-full transition disabled:opacity-30 ${t.attach}`}
    >
      <PlusIcon className={t.attachIcon} />
    </button>
  )

  const send = (
    <button
      type="button"
      aria-label="Send"
      disabled={!value.trim() || disabled}
      onClick={onSend}
      className={`flex items-center justify-center rounded-full transition disabled:opacity-30 ${t.send} ${
        value.trim() && !disabled ? t.sendOn : t.sendOff
      }`}
    >
      <ArrowUpIcon className={t.sendIcon} />
    </button>
  )

  const textarea = (
    <textarea
      ref={inputRef}
      autoFocus={autoFocus}
      value={value}
      rows={rows}
      disabled={disabled}
      placeholder="Describe your edit..."
      onChange={(event) => onChange(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === 'Escape') onEscape?.()
        if (event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault()
          onSend()
        }
      }}
      className={`w-full resize-none overflow-y-auto bg-transparent focus:outline-none disabled:opacity-50 [scrollbar-width:thin] ${t.textarea}`}
    />
  )

  if (dark) {
    return (
      <>
        {textarea}
        {attach}
        {send}
      </>
    )
  }

  return (
    <div className="relative rounded-2xl border border-gray-300 bg-gray-50 px-3 pt-3 pb-10 transition focus-within:border-purple-400 focus-within:bg-white">
      {textarea}
      <div className="absolute right-2.5 bottom-2.5 flex items-center gap-1.5">
        {attach}
        {send}
      </div>
    </div>
  )
}
